import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Tag, Space, message, Button, Select, Popconfirm, Tooltip, Modal, Form, Input, InputNumber, Switch } from 'antd';
import { PlusOutlined, EditOutlined, CopyOutlined, CheckCircleOutlined, StopOutlined, FilterOutlined, BankOutlined } from '@ant-design/icons';
import { getChannelLogo, getCurrencyIcon, buildChannelOptions } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';
import { testApi } from '../services/api';
import { getEnvByHostname } from '../utils/env';

const TYPE_COLOR = { ALL: 'blue', PAYIN: 'green', PAYOUT: 'orange', BALANCE: 'purple' };
const ACCT_TYPE_COLOR = { MAIN: 'gold', SUB: 'cyan', BACKUP: 'default' };

export default function ChannelAccountManagement() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filterChannel, setFilterChannel] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterMerchant, setFilterMerchant] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [demoAccountKeys, setDemoAccountKeys] = useState(new Set());
  const [channelCurrencyMap, setChannelCurrencyMap] = useState({});
  const [selectedChannel, setSelectedChannel] = useState(null);

  const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) { try { return JSON.parse(userStr).username; } catch (e) {} }
    return localStorage.getItem('username') || 'admin';
  };

  const callApi = useCallback(async (testType, body = {}) => {
    const response = await fetch('/inner/v1/fiat-payment/test/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ testType, executor: getCurrentUser(), env: getEnvByHostname(), ...body })
    });
    return response.json();
  }, []);

  // Channel options from account data
  const channelOptions = useMemo(() => {
    const codes = [...new Set(accounts.map(a => a.channelCode).filter(Boolean))].sort();
    return buildChannelOptions(codes);
  }, [accounts]);

  // Merchant options from account data
  const merchantOptions = useMemo(() => {
    const bps = [...new Set(accounts.map(a => a.businessPartner).filter(Boolean))].sort();
    return bps.map(bp => ({ label: bp, value: bp }));
  }, [accounts]);

  // Filtered data
  const filteredAccounts = useMemo(() => accounts.filter(a => {
    if (filterChannel && a.channelCode !== filterChannel) return false;
    if (filterStatus !== null && a.status !== filterStatus) return false;
    if (filterMerchant && a.businessPartner !== filterMerchant) return false;
    return true;
  }), [accounts, filterChannel, filterStatus, filterMerchant]);

  // Currency options for form
  const currencyOptions = useMemo(() => {
    if (selectedChannel && channelCurrencyMap[selectedChannel]?.length > 0) {
      return channelCurrencyMap[selectedChannel].map(c => ({ label: `${getCurrencyIcon(c)} ${c}`, value: c }));
    }
    const allCurrencies = new Set();
    Object.values(channelCurrencyMap).forEach(arr => arr.forEach(c => allCurrencies.add(c)));
    return [...allCurrencies].sort().map(c => ({ label: `${getCurrencyIcon(c)} ${c}`, value: c }));
  }, [selectedChannel, channelCurrencyMap]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await callApi('CHANNEL_ACCOUNTS_LIST');
      if (result.code === '200' || result.code === 200) {
        const list = result.data || [];
        setAccounts(list);
        // Build demo account keys
        const keys = new Set();
        list.filter(a => a.businessPartner === 'DEMO_ACCOUNT').forEach(a => {
          if (a.channelCode && a.currency) keys.add(`${a.channelCode}_${a.currency}`);
        });
        setDemoAccountKeys(keys);
      } else {
        message.error(result.msg || t('loadFailed'));
      }
    } catch { message.error(t('loadFailed')); }
    finally { setLoading(false); }
  }, [callApi, t]);

  // Load channel-currency mapping
  useEffect(() => {
    (async () => {
      try {
        const res = await testApi.getChannelCurrencies();
        const data = res?.data?.data || res?.data || {};
        const currMap = {};
        if (typeof data === 'object') {
          Object.entries(data).forEach(([channel, currencies]) => {
            if (Array.isArray(currencies)) currMap[channel] = currencies.sort();
          });
        }
        setChannelCurrencyMap(currMap);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleToggleStatus = async (accountId, currentStatus) => {
    try {
      const testType = currentStatus === 1 ? 'CHANNEL_ACCOUNT_DEACTIVATE' : 'CHANNEL_ACCOUNT_ACTIVATE';
      const result = await callApi(testType, { requestBody: JSON.stringify({ accountId }) });
      if (result.code === '200' || result.code === 200) {
        message.success(t(currentStatus === 1 ? 'disable' : 'enable') + t('success'));
        loadAccounts();
      } else { message.error(result.msg || t('operationFailed')); }
    } catch { message.error(t('operationFailed')); }
  };

  const handleClone = async (accountId) => {
    try {
      const result = await callApi('CHANNEL_ACCOUNT_COPY', { requestBody: JSON.stringify({ accountId }) });
      if (result.code === '200' || result.code === 200) {
        message.success(t('createSuccess'));
        loadAccounts();
      } else { message.error(result.msg || t('operationFailed')); }
    } catch { message.error(t('operationFailed')); }
  };

  const openCreate = () => {
    setEditRecord(null);
    setSelectedChannel(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, priority: 0, userType: 'ALL', accountType: 'MAIN', clientType: 'ALL' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    setSelectedChannel(record.channelCode);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const testType = editRecord ? 'CHANNEL_ACCOUNT_UPDATE' : 'CHANNEL_ACCOUNT_CREATE';
      const payload = editRecord ? { ...values, id: editRecord.id } : values;
      const result = await callApi(testType, { requestBody: JSON.stringify(payload) });
      if (result.code === '200' || result.code === 200) {
        message.success(editRecord ? t('updateSuccess') : t('createSuccess'));
        setModalOpen(false);
        loadAccounts();
      } else { message.error(result.msg || t('operationFailed')); }
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || t('operationFailed'));
    } finally { setSaving(false); }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60, sorter: (a, b) => (a.id || 0) - (b.id || 0), defaultSortOrder: 'descend' },
    { title: t('channelCode'), dataIndex: 'channelCode', width: 140,
      render: v => { const l = getChannelLogo(v); return <span style={{ fontWeight: 500 }}>{l.emoji} {v}</span>; } },
    { title: t('clientType'), dataIndex: 'clientType', width: 90,
      render: v => <Tag color={TYPE_COLOR[v] || 'default'}>{v || '—'}</Tag> },
    { title: t('currency'), dataIndex: 'currency', width: 80,
      render: v => <span>{getCurrencyIcon(v)} {v}</span> },
    { title: 'BusinessPartner', dataIndex: 'businessPartner', width: 160,
      render: v => v ? <span style={{ fontWeight: 500 }}>{v}</span> : '—' },
    { title: t('userType'), dataIndex: 'userType', width: 80,
      render: v => v ? <Tag color={v === 'ALL' ? 'blue' : v === 'BUSINESS' ? 'cyan' : 'geekblue'}>{v}</Tag> : '—' },
    { title: t('accountType'), dataIndex: 'accountType', width: 90,
      render: v => v ? <Tag color={ACCT_TYPE_COLOR[v] || 'default'}>{v}</Tag> : '—' },
    { title: t('localAccountNo'), dataIndex: 'accountNo', width: 200, ellipsis: true,
      render: v => v ? <Tooltip title={v}><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span></Tooltip> : '—' },
    { title: t('priority'), dataIndex: 'priority', width: 60, align: 'center' },
    { title: t('status'), dataIndex: 'status', width: 70, align: 'center',
      render: v => v === 1 ? <Tag color="success">{t('active')}</Tag> : <Tag color="default">{t('disabled')}</Tag> },
    { title: t('operation'), key: 'action', width: 260, fixed: 'right',
      render: (_, r) => {
        const showClone = r.businessPartner !== 'DEMO_ACCOUNT' && r.status === 1
          && !demoAccountKeys.has(`${r.channelCode}_${r.currency}`);
        return (
          <Space size={6}>
            <a style={{ color: '#667eea', cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <EditOutlined style={{ marginRight: 3 }} />{t('edit')}
            </a>
            {showClone && (
              <Popconfirm title={t('cloneToDemo')} onConfirm={() => handleClone(r.id)}>
                <span style={{ color: '#52c41a', cursor: 'pointer', padding: '2px 8px', background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f', fontSize: 12 }}>
                  <CopyOutlined /> Canary
                </span>
              </Popconfirm>
            )}
            <Popconfirm
              title={r.status === 1 ? `${t('confirm')} ${t('disable')}?` : `${t('confirm')} ${t('enable')}?`}
              onConfirm={() => handleToggleStatus(r.id, r.status)}
            >
              {r.status === 1
                ? <span style={{ color: '#ff4d4f', cursor: 'pointer', padding: '2px 8px', background: '#fff2f0', borderRadius: 4, border: '1px solid #ffccc7', fontSize: 12 }}>
                    <StopOutlined /> {t('disable')}
                  </span>
                : <span style={{ color: '#1677ff', cursor: 'pointer', padding: '2px 8px', background: '#e6f4ff', borderRadius: 4, border: '1px solid #91caff', fontSize: 12 }}>
                    <CheckCircleOutlined /> {t('enable')}
                  </span>}
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      <div className="fade-in-up">
        {/* Header with filters */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space size={12}>
            <BankOutlined style={{ color: '#667eea', fontSize: 20 }} />
            <span style={{ fontSize: 20, fontWeight: 600 }}>{t('channelAccountManagement')}</span>
          </Space>
          <Space size={12}>
            <FilterOutlined style={{ color: '#667eea' }} />
            <Select allowClear showSearch optionFilterProp="label" placeholder={t('allChannels')}
              style={{ width: 180 }} value={filterChannel} onChange={setFilterChannel} options={channelOptions} />
            <Select allowClear placeholder={t('allStatuses')} style={{ width: 110 }}
              value={filterStatus} onChange={setFilterStatus}
              options={[
                { label: <Tag color="success" style={{ margin: 0 }}>{t('active')}</Tag>, value: 1 },
                { label: <Tag color="default" style={{ margin: 0 }}>{t('disabled')}</Tag>, value: 0 },
              ]} />
            <Select allowClear showSearch optionFilterProp="label" placeholder="BusinessPartner"
              style={{ width: 180 }} value={filterMerchant} onChange={setFilterMerchant} options={merchantOptions} />
            <span style={{ color: '#9ca3af', fontSize: 13 }}>{t('totalCount', filteredAccounts.length)}</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('create')}</Button>
          </Space>
        </div>

        <Table columns={columns} dataSource={filteredAccounts} rowKey="id" loading={loading}
          size="middle" scroll={{ x: 1400 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: n => t('totalCount', n) }}
        />

        {/* Create / Edit Modal */}
        <Modal
          title={editRecord ? `${t('edit')} - ${t('channelAccountManagement')}` : `${t('create')} - ${t('channelAccountManagement')}`}
          open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave}
          confirmLoading={saving} width={860} destroyOnClose
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            {/* Basic Info Section */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' }}>
              🏦 {t('basicConfig')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Form.Item name="channelCode" label={t('channelCode')} rules={[{ required: true, message: t('pleaseSelect', t('channelCode')) }]}>
                <Select disabled={!!editRecord} showSearch optionFilterProp="label"
                  placeholder={t('selectChannel')} options={channelOptions}
                  onChange={v => { setSelectedChannel(v); form.setFieldValue('currency', undefined); }} />
              </Form.Item>
              <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: t('pleaseSelect', t('currency')) }]}>
                <Select showSearch allowClear placeholder={t('selectCurrency')} options={currencyOptions}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
              </Form.Item>
              <Form.Item name="businessPartner" label="BusinessPartner" rules={[{ required: true }]}>
                <Input placeholder="e.g. test / DEMO_ACCOUNT" />
              </Form.Item>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
              <Form.Item name="clientType" label={t('clientType')}>
                <Select options={[{ value: 'ALL' }, { value: 'PAYIN' }, { value: 'PAYOUT' }, { value: 'BALANCE' }]} />
              </Form.Item>
              <Form.Item name="userType" label={t('userType')}>
                <Select options={[{ value: 'ALL' }, { value: 'BUSINESS' }, { value: 'INDIVIDUAL' }]} />
              </Form.Item>
              <Form.Item name="accountType" label={t('accountType')}>
                <Select options={[{ value: 'MAIN' }, { value: 'SUB' }, { value: 'BACKUP' }]} />
              </Form.Item>
              <Form.Item name="status" label={t('status')}>
                <Select options={[{ label: t('active'), value: 1 }, { label: t('disabled'), value: 0 }]} />
              </Form.Item>
            </div>

            {/* Account Info Section */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>
              📋 {t('localAccountNo')} / {t('channelAccountNo')}
            </div>
            {editRecord ? (
              <Form.Item name="accountNo" label={t('localAccountNo')}>
                <Input disabled style={{ fontFamily: 'monospace', background: '#f5f5f5' }} />
              </Form.Item>
            ) : (
              <div style={{ padding: '8px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, fontSize: 12, color: '#1d39c4', marginBottom: 16 }}>
                💡 {t('localAccountNo')} — {t('autoGenerate')}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="channelAccountNo" label={t('channelAccountNo')}>
                <Input style={{ fontFamily: 'monospace' }} />
              </Form.Item>
              <Form.Item name="customerId" label={t('customerId')}>
                <Input />
              </Form.Item>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr 1fr', gap: 16 }}>
              <Form.Item name="priority" label={t('priority')}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="endpoint" label={t('endpoint')}>
                <Input placeholder="https://api.example.com" />
              </Form.Item>
              <Form.Item name="timeoutMs" label={t('timeoutMs')}>
                <InputNumber min={1000} step={1000} style={{ width: '100%' }} placeholder="30000" />
              </Form.Item>
            </div>

            {/* Security Section */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>
              🔑 {t('security')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="apiKey" label="API Key"><Input /></Form.Item>
              <Form.Item name="secretKey" label="Secret Key"><Input.Password /></Form.Item>
            </div>
            <Form.Item name="signAlgorithm" label={t('signAlgorithm')}>
              <Select allowClear showSearch placeholder={t('selectAlgorithm')} popupMatchSelectWidth={false}
                options={[
                  { value: 'SHA384withECDSA', label: 'SHA384withECDSA (ECC)' },
                  { value: 'SHA256withECDSA', label: 'SHA256withECDSA (ECC)' },
                  { value: 'SHA256withRSA', label: 'SHA256withRSA (RSA)' },
                  { value: 'HmacSHA256', label: 'HmacSHA256 (HMAC)' },
                  { value: 'HmacSHA512', label: 'HmacSHA512 (HMAC)' },
                  { value: 'MD5', label: 'MD5' },
                ]} />
            </Form.Item>
            <Form.Item name="channelPublicKey" label={t('channelPublicKey')}>
              <Input.TextArea rows={2} style={{ fontFamily: 'monospace', fontSize: 11 }} />
            </Form.Item>

            {/* Extra Config */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>
              🔧 {t('extraConfig')}
            </div>
            <Form.Item name="extraConfig">
              <Input.TextArea rows={3} placeholder="JSON" style={{ fontFamily: 'monospace', fontSize: 12 }} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
}
